// Hideo - dragging files and text into the app (from the computer or from another browser tab). Ported from the legacy
// ui/drop.js (verbatim copy in tests/fixtures/legacy/drop.js, golden tests in tests/drop.test.ts).
// - Board (columns), Calendar (days, Week view too) and Timeline (rows): an image, an audio, a .txt/.md file or some text
//   → a new video there (dropNewVideo, called by BoardPage.vue, CalendarPage.vue, TimelinePage.vue).
// - Sources: a link → a new source with that link. Hooks: some text → a new hook (installPageDrops: listened on the
//   document, so the Vue pages work whenever they are created; installed once by the shell, App.vue).
// - Script editor: a .txt/.md file is inserted where it is dropped (installScriptDrop on #scriptBody / #scriptMd; the
//   editor's state, its mode and its autosave, are in the editor store, src/stores/editor.ts).
// Elsewhere: the video dialog (an audio becomes the voice note, an image the thumbnail: VideoDialog.vue), the Profiles
// window (a .json save file dropped on a profile: ProfilesDialog.vue), the photo boxes (ImageBox.vue) and the idea box
// (IdeaDrop.vue) handle their own drops. A file dropped anywhere else does nothing (the file-drop guard of src/utils/image.ts stops the browser
// from opening it).
import { t } from '../i18n'
import type { ProfileData, Script, Video } from '../data/types'
import { ytThumb } from '../utils/url'
import { cleanHtml } from './editor/html'
import { mdToHtml } from './editor/markdown'
import { fountainTabHtml } from './editor/fountain'
import type { HookPrefill } from '../stores/dialogs'
import { newVideo } from '../data/newVideo'
import { firstLine } from '../data/items'

/** The first file of a drop, or null */
export const dropFile = (e: { dataTransfer?: DataTransfer | null }): File | null => [...((e.dataTransfer && e.dataTransfer.files) || [])][0] || null
/** The link or text of a drop ('' when none) */
export const dropText = (e: { dataTransfer?: DataTransfer | null }): string =>
  ((e.dataTransfer && (e.dataTransfer.getData('text/uri-list') || e.dataTransfer.getData('text/plain'))) || '').trim()
/** A text file: .md, .markdown, .txt, .fountain, or a text/plain or text/markdown type */
export const isTextFile = (f: { name: string; type: string }): boolean => /\.(md|markdown|txt|fountain)$/i.test(f.name) || f.type === 'text/plain' || f.type === 'text/markdown'
/** A Fountain screenplay file */
export const isFountainFile = (f: { name?: string }): boolean => /\.fountain$/i.test(f.name || '')
/** Title from a file name: no extension, _ and - as spaces, at most 100 characters ("Untitled" when nothing is left) */
export const fileTitle = (f: { name: string }): string => f.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim().slice(0, 100) || t('Untitled')
/** Largest audio file accepted as a voice note (10 MB) */
export const MAX_AUDIO = 10e6

/** Where a new video dropped on the plan goes */
export interface DropVideoOpts { status?: string; publishDate?: string; publishTime?: string; workStart?: string }

/** The new video of a drop before its file or text is read. Default titles are created in the UI language; status,
 *  priority and reelProva are stored values. Fields in the stored order (users' files stay byte-identical) */
export function droppedVideoBase(d: ProfileData, projectId: string, opts: DropVideoOpts, now = Date.now()): Video {
  return newVideo(d, { id: 'v_' + now, title: t('Untitled'), projectId, status: opts.status, publishDate: opts.publishDate, publishTime: opts.publishTime },
    { workStart: opts.workStart || '' })   // Timeline work start; the end is the publish date
}
/** A dropped text file becomes a script of the video (first tab "First draft"; a .fountain file a screenplay in
 *  Fountain mode); the script goes into the data. The ids and the time are taken before the file is read, like before */
export async function droppedScript(d: ProfileData, v: Video, f: Pick<Blob, 'text'> & { name?: string }, now = Date.now): Promise<Script> {
  const fountain = isFountainFile(f)
  const sc = { id: 'sc_' + now(), title: v.title, videoId: v.id, mode: fountain ? 'fountain' : 'visual', updatedAt: now(), tabs: [{ id: 'tb_' + now(), name: t('First draft'), html: '' }] } as Script
  const text = await f.text()
  sc.tabs![0].html = fountain ? fountainTabHtml(text.replace(/\r\n?/g, '\n')) : cleanHtml(mdToHtml(text))
  sc.activeTab = sc.tabs![0].id
  d.scripts.push(sc)
  v.scriptId = sc.id
  return sc
}
/** Dropped text: a link becomes the video's link (a YouTube one also its thumbnail), other text the title (first line,
 *  at most 100 characters) and, when longer, the notes */
export function droppedTextInto(v: Video, text: string): void {
  if (/^https?:\/\//i.test(text)) {
    v.link = text.split('\n')[0]
    v.title = ytThumb(v.link) ? t('Video from YouTube') : t('New video')
    if (ytThumb(v.link)) v.thumbnail = ytThumb(v.link)
  } else {
    const first = text.split('\n')[0].trim()
    v.title = first.slice(0, 100)
    if (text.length > first.length) v.notes = text
  }
}
/** Text dropped on the Hooks page → the new hook's title (first line, at most 60 characters) and content */
export const droppedHook = (txt: string): HookPrefill => ({ title: firstLine(txt).slice(0, 60), content: txt })

/** The page box (#view-sources, #view-vault) a drag is over, or null */
const zoneAt = (e: Event, viewId: string): HTMLElement | null => {
  const el = e.target as Element | null
  return el && el.closest ? el.closest<HTMLElement>('#' + viewId) : null
}

/** Listens on `doc` for links and text dropped on a page box: the box lights up (.drag-over) while text is over it; a
 *  file is left alone (no new item) */
export function dropZone(doc: Pick<Document, 'addEventListener'>, viewId: string, onDrop: (txt: string) => void): void {
  doc.addEventListener('dragover', e => {
    const v = zoneAt(e, viewId)
    if (!v) return
    const types = [...(e as DragEvent).dataTransfer!.types]
    if (types.includes('text/plain') || types.includes('text/uri-list')) { e.preventDefault(); v.classList.add('drag-over') }
  })
  doc.addEventListener('dragleave', e => { const v = zoneAt(e, viewId); if (v && !v.contains((e as DragEvent).relatedTarget as Node | null)) v.classList.remove('drag-over') })
  doc.addEventListener('drop', e => {
    const v = zoneAt(e, viewId)
    if (!v) return
    v.classList.remove('drag-over')
    if (dropFile(e as DragEvent)) return
    const txt = dropText(e as DragEvent)
    if (!txt) return
    e.preventDefault()
    onDrop(txt)
  })
}
